import Image from 'next/image';
import styled from 'styled-components';

import RichText from 'components/RichText';
import Slider from 'components/Slider';

import BREAKPOINTS from 'styles/breakpoints';
import COLORS from 'styles/colors';
import TYPO, { rem, weight } from 'styles/typography';

const Section = styled.section`
  margin: 0 auto 45px;
  max-width: 1284px;

  ${BREAKPOINTS.small`
    margin: 50px auto 0;
  `}

  ${BREAKPOINTS.large`
    margin: 100px auto 50px;
  `}
`;

const Container = styled.div`
  margin: 0 17px;
  position: relative;
  max-width: 1230px;
  border-radius: 5px;
  background-color: ${COLORS.white};

  .slick-dots {
    margin-bottom: 30px;
    bottom: 0;

    li {
      margin: 0 5px;
      width: 10px;
      height: 10px;

      &.slick-active button::before {
        background-color: ${COLORS.dawn};
      }

      button:before {
        background-color: ${COLORS.forestBlack.opacity(0.05)};
      }
    }
  }

  ${BREAKPOINTS.small`
    margin: 0 var(--gutter-side);
  `}

  ${BREAKPOINTS.large`
    padding: 0 var(--gutter-side);
  `}
`;

const QuoteFlourish = styled.div`
  padding: 60px 0 13px;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  text-align: center;

  ${BREAKPOINTS.small`
    padding-top: 50px;
  `}
`;

const QuoteIcon = styled.div`
  margin: 0 10px;
  display: inline-block;
`;

const Item = styled.div`
  padding: 112px 8px 40px;
  color: ${COLORS.forestDark};
  text-align: center;

  ${BREAKPOINTS.small`
    padding-top: 102px;
  `}
`;

const RichTextStyled = styled(RichText)`
  margin: 0 auto;
  max-width: 670px;

  * {
    ${TYPO.wulkan}
    margin: 0;
    font-size: ${rem(30)};
    line-height: ${rem(36)};
    font-style: italic;
    font-weight: ${weight.bold};
    opacity: 1;
  }

  u,
  em,
  strong {
    color: ${COLORS.dawn};
    font-style: italic;
    text-decoration: none;
    font-weight: ${weight.bold};
  }
`;

const Cite = styled.cite`
  margin: 20px auto 28px;
  display: block;
  max-width: 485px;
  font-style: normal;
  color: ${COLORS.forestDark.opacity(0.8)};
`;

const Span = styled.span`
  ${TYPO.wulkan}
  font-style: italic;
  font-weight: ${weight.bold};
  color: ${COLORS.dawn};
`;

const ArrowNext = styled.button`
  display: none !important;
  margin: 0 50px;
  top: 50%;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${COLORS.blueGrey.opacity(0.05)}
    url('/images/icons/arrow-right-dawn.svg') no-repeat center;
  transform: translateY(-50%) rotate(-180deg);
  z-index: 10;

  ${BREAKPOINTS.large`
    display: block !important;
  `}

  ${BREAKPOINTS.xLarge`
    margin: 0 98px;
  `}
`;

const ArrowPrev = styled(ArrowNext)`
  right: 0;
  transform: translateY(-50%) rotate(0);
`;

const TestimonialsSlider = ({ items = [] }) => {
  return (
    <Section>
      <Container>
        <QuoteFlourish>
          <QuoteIcon>
            <Image
              src="/images/quotation-flourish-solid.png"
              width={38}
              height={32}
            />
          </QuoteIcon>
          <QuoteIcon>
            <Image
              src="/images/quotation-flourish-outline.png"
              width={37}
              height={32}
            />
          </QuoteIcon>
        </QuoteFlourish>

        <Slider
          arrows={true}
          infinite={true}
          nextArrow={<ArrowNext />}
          prevArrow={<ArrowPrev />}
        >
          {items.map(({ id, testimonial, author, role }) => (
            <Item key={id}>
              <RichTextStyled html={testimonial?.html} />
              <Cite>
                {author} {role && <Span>— {role}</Span>}
              </Cite>
            </Item>
          ))}
        </Slider>
      </Container>
    </Section>
  );
};

export default TestimonialsSlider;
